import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../Layout/DashboardLayout';
import SuperAdminLayout from '../Layout/SuperAdminLayout';

// Admin Pages
import AdminOverview from './admin/AdminOverview';
import EmployeesPage from './admin/EmployeesPage';
import EmployeeDetailPage from './admin/EmployeeDetailPage';
import EmployeeFormPage from './admin/EmployeeFormPage';
import CustomersPage from './admin/CustomersPage';
import LocationsPage from './admin/LocationsPage';
import AdminLeavesPage from './admin/AdminLeavesPage';
import AdminPayrollPage from './admin/AdminPayrollPage';
import PFEcrPage from './admin/PFEcrPage';
import BonusPage from './admin/BonusPage';
import AdminSettingsPage from './admin/AdminSettingsPage';
import AdminRegularizationPage from './admin/AdminRegularizationPage';
import AdminLettersPage from './admin/AdminLettersPage';
import HolidayManagement from './HolidayManagement';
import EmployeeLocationTracker from './admin/EmployeeLocationTracker';
import AdvanceManagement from './admin/AdvanceManagement';
import ExpensesPage from './admin/ExpensesPage';
import LndPage from './admin/LndPage';
import MoodDashboardPage from './admin/MoodDashboardPage';
import PmsPage from './admin/PmsPage';
import PoliciesPage from './admin/PoliciesPage';
import RewardsPage from './admin/RewardsPage';
import AssetsPage from './admin/AssetsPage';
import EncashmentConfigPage from './admin/EncashmentConfigPage';
import EncashmentRequestsPage from './admin/EncashmentRequestsPage';
import SupportCentre from './SupportCentre';

// Employee Pages
import EmployeeOverview from './employee/EmployeeOverview';
import AttendancePage from './employee/AttendancePage';
import MyCustomersPage from './employee/MyCustomersPage';
import EmployeeLeavesPage from './employee/EmployeeLeavesPage';
import EmployeePayrollPage from './employee/EmployeePayrollPage';
import EmployeeLettersPage from './employee/EmployeeLettersPage';
import RegularizationPage from './employee/RegularizationPage';
import MyProfilePage from './employee/MyProfilePage';
import RequestAdvance from './employee/RequestAdvance';
import MyAdvanceRequests from './employee/MyAdvanceRequests';
import MyExpensesPage from './employee/MyExpensesPage';
import MyLearningPage from './employee/MyLearningPage';
import MyGoalsPage from './employee/MyGoalsPage';
import MyPoliciesPage from './employee/MyPoliciesPage';
import MyRewardsPage from './employee/MyRewardsPage';
import MyAssetsPage from './employee/MyAssetsPage';

// Resignation Pages
import EmployeeResignation from './EmployeeResignation';
import ManagerResignations from './ManagerResignations';
import ResignationManagement from './ResignationManagement';

// Team Pages (for Reporting Managers)
import TeamMembersPage from './employee/TeamMembersPage';
import TeamAttendancePage from './employee/TeamAttendancePage';
import TeamLeavesPage from './employee/TeamLeavesPage';
import TeamRegularizationPage from './employee/TeamRegularizationPage';

// SuperAdmin Pages
import SuperAdminOverview from './superadmin/SuperAdminOverview';
import ClientsPage from './superadmin/ClientsPage';
import ClientFormPage from './superadmin/ClientFormPage';
import AnalyticsPage from './superadmin/AnalyticsPage';
import SuperAdminSettingsPage from './superadmin/SuperAdminSettingsPage';
import ExpendituresPage from './superadmin/ExpendituresPage';
import ProfitsPage from './superadmin/ProfitsPage';
import ContactSubmissionsPage from './superadmin/ContactSubmissionsPage';
import SeparationsPage from './superadmin/SeparationsPage';
import PlatformSupportInbox from './superadmin/PlatformSupportInbox';
import StaffManagementPage from './superadmin/StaffManagementPage';
import StaffFormPage from './superadmin/StaffFormPage';
import QuotationsPage from './superadmin/QuotationsPage';
import BillingPage from './superadmin/BillingPage';
import AnnouncementsPage from './superadmin/AnnouncementsPage';
import AuditLogPage from './superadmin/AuditLogPage';

// Recruitment / Talent Acquisition Pages
import { RecruitmentDashboard, CandidatePipeline, InterviewerDashboard, HROfferDashboard } from './recruitment';

// Shared Pages
import ChangePasswordPage from './pages/ChangePasswordPage';

const DashboardRouter = () => {
  const { user } = useAuth();

  const renderRoutes = () => {
    if (user?.role === 'superadmin') {
      return (
        <Routes>
          <Route index element={<SuperAdminOverview />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/add" element={<ClientFormPage />} />
          <Route path="clients/:id/edit" element={<ClientFormPage />} />
          <Route path="expenditures" element={<ExpendituresPage />} />
          <Route path="profits" element={<ProfitsPage />} />
          <Route path="quotations" element={<QuotationsPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="audit-log" element={<AuditLogPage />} />
          <Route path="separations" element={<SeparationsPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="contact-submissions" element={<ContactSubmissionsPage />} />
          <Route path="support" element={<PlatformSupportInbox />} />
          <Route path="staff" element={<StaffManagementPage />} />
          <Route path="staff/add" element={<StaffFormPage />} />
          <Route path="staff/:id/edit" element={<StaffFormPage />} />
          <Route path="settings" element={<SuperAdminSettingsPage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      );
    }

    if (user?.role === 'platform_staff') {
      // DigiHR-internal staff: same page components as superadmin, but each route only
      // rendered when the corresponding module's 'read' permission is granted. Staff
      // Management itself is deliberately superadmin-only (see routes/platformStaff.js).
      const perms = user?.platformPermissions || {};
      const canRead = (mod) => perms[mod]?.read === true;
      return (
        <Routes>
          <Route index element={<SuperAdminOverview />} />
          {canRead('clients') && <Route path="clients" element={<ClientsPage />} />}
          {perms.clients?.create && <Route path="clients/add" element={<ClientFormPage />} />}
          {perms.clients?.update && <Route path="clients/:id/edit" element={<ClientFormPage />} />}
          {canRead('expenditures') && <Route path="expenditures" element={<ExpendituresPage />} />}
          {canRead('profits') && <Route path="profits" element={<ProfitsPage />} />}
          {canRead('quotations') && <Route path="quotations" element={<QuotationsPage />} />}
          {canRead('billing') && <Route path="billing" element={<BillingPage />} />}
          {canRead('announcements') && <Route path="announcements" element={<AnnouncementsPage />} />}
          {canRead('audit') && <Route path="audit-log" element={<AuditLogPage />} />}
          {canRead('separations') && <Route path="separations" element={<SeparationsPage />} />}
          {canRead('analytics') && <Route path="analytics" element={<AnalyticsPage />} />}
          {canRead('contactForms') && <Route path="contact-submissions" element={<ContactSubmissionsPage />} />}
          {canRead('platformSupport') && <Route path="support" element={<PlatformSupportInbox />} />}
          {canRead('settings') && <Route path="settings" element={<SuperAdminSettingsPage />} />}
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      );
    }

    if (user?.role === 'admin') {
      return (
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="employees/add" element={<EmployeeFormPage />} />
          <Route path="employees/:id" element={<EmployeeDetailPage />} />
          <Route path="employees/:id/edit" element={<EmployeeFormPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="holidays" element={<HolidayManagement />} />
          <Route path="leaves" element={<AdminLeavesPage />} />
          <Route path="regularization" element={<AdminRegularizationPage />} />
          <Route path="location-tracker" element={<EmployeeLocationTracker />} />
          <Route path="payroll" element={<AdminPayrollPage />} />
          <Route path="pf-ecr" element={<PFEcrPage />} />
          <Route path="bonus" element={<BonusPage />} />
          <Route path="letters" element={<AdminLettersPage />} />
          <Route path="advances" element={<AdvanceManagement />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="lnd" element={<LndPage />} />
          <Route path="mood" element={<MoodDashboardPage />} />
          <Route path="pms" element={<PmsPage />} />
          <Route path="policies" element={<PoliciesPage />} />
          <Route path="rewards" element={<RewardsPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="encashment/config" element={<EncashmentConfigPage />} />
          <Route path="encashment/requests" element={<EncashmentRequestsPage />} />
          <Route path="resignations" element={<ResignationManagement />} />
          <Route path="support" element={<SupportCentre />} />
          {/* Recruitment / Talent Acquisition */}
          <Route path="recruitment" element={<RecruitmentDashboard />} />
          <Route path="recruitment/pipeline/:jobId" element={<CandidatePipeline />} />
          <Route path="recruitment/offers" element={<HROfferDashboard />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      );
    }

    // Employee/Marketing role
    // Check special permissions for payroll and letters admin access
    const canManagePayroll = user?.permissions?.canGeneratePayroll === true;
    const canManageLetters = user?.permissions?.canGenerateLetters === true;
    const canManageAdvances = user?.permissions?.canManageAdvances === true;
    const canManageEncashment = user?.permissions?.canManageEncashment === true;
    const canViewMoodAnalytics = user?.permissions?.canViewMoodAnalytics === true;
    // Check if user has TA role (hod, hr_head, ta_executive) OR is assigned as HOD OR has assigned jobs
    // Grants the recruitment overview + candidate Kanban board (job-request creation lives here for HOD)
    const hasTARole = ['hod', 'hr_head', 'ta_executive'].includes(user?.role) || user?.isHOD === true || user?.hasAssignedJobs === true;
    // Manager/HOD get their own scoped interview-feedback dashboard - never the shared Kanban actions
    // (CandidateCard.jsx already hides all mutating controls from non-TA roles regardless)
    const isInterviewer = ['manager', 'hod'].includes(user?.role);
    // HR owns the CTC/offer stage
    const isHR = user?.role === 'hr_head';

    return (
      <Routes>
        <Route index element={<EmployeeOverview />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="regularization" element={<RegularizationPage />} />
        <Route path="customers" element={<MyCustomersPage />} />
        <Route path="leaves" element={<EmployeeLeavesPage />} />
        <Route path="payroll" element={canManagePayroll ? <AdminPayrollPage /> : <EmployeePayrollPage />} />
        {canManagePayroll && <Route path="pf-ecr" element={<PFEcrPage />} />}
        {canManagePayroll && <Route path="bonus" element={<BonusPage />} />}
        {canViewMoodAnalytics && <Route path="mood" element={<MoodDashboardPage />} />}
        <Route path="letters" element={canManageLetters ? <AdminLettersPage /> : <EmployeeLettersPage />} />
        <Route path="request-advance" element={<RequestAdvance />} />
        <Route path="my-advances" element={<MyAdvanceRequests />} />
        <Route path="my-expenses" element={<MyExpensesPage />} />
        <Route path="my-learning" element={<MyLearningPage />} />
        <Route path="my-goals" element={<MyGoalsPage />} />
        <Route path="my-policies" element={<MyPoliciesPage />} />
        <Route path="my-rewards" element={<MyRewardsPage />} />
        <Route path="my-assets" element={<MyAssetsPage />} />
        <Route path="support" element={<SupportCentre />} />
        {canManageAdvances && <Route path="advances" element={<AdvanceManagement />} />}
        {canManageEncashment && <Route path="encashment/config" element={<EncashmentConfigPage />} />}
        {canManageEncashment && <Route path="encashment/requests" element={<EncashmentRequestsPage />} />}
        <Route path="resignation" element={<EmployeeResignation />} />
        <Route path="my-profile" element={<MyProfilePage />} />
        {/* Team routes for Reporting Managers */}
        <Route path="team-members" element={<TeamMembersPage />} />
        <Route path="team-attendance" element={<TeamAttendancePage />} />
        <Route path="team-leaves" element={<TeamLeavesPage />} />
        <Route path="team-regularization" element={<TeamRegularizationPage />} />
        <Route path="team-resignations" element={<ManagerResignations />} />
        {/* Recruitment / Talent Acquisition (for TA roles) */}
        {hasTARole && (
          <>
            <Route path="recruitment" element={<RecruitmentDashboard />} />
            <Route path="recruitment/pipeline/:jobId" element={<CandidatePipeline />} />
          </>
        )}
        {/* Manager/HOD interview feedback dashboard - scheduling/moving stages is TA-only */}
        {isInterviewer && (
          <Route path="recruitment/interviews" element={<InterviewerDashboard roundType={user?.role === 'hod' ? 'hod' : 'manager'} />} />
        )}
        {/* HR CTC/offer dashboard */}
        {isHR && (
          <Route path="recruitment/offers" element={<HROfferDashboard />} />
        )}
        <Route path="change-password" element={<ChangePasswordPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    );
  };

  // Superadmin + DigiHR platform staff get the dedicated dark-sidebar shell;
  // client-side roles (admin/employee/manager) keep the standard layout.
  const isPlatformRole = user?.role === 'superadmin' || user?.role === 'platform_staff';
  const Shell = isPlatformRole ? SuperAdminLayout : DashboardLayout;

  return <Shell>{renderRoutes()}</Shell>;
};

export default DashboardRouter;
